import type { ProjectType } from "../projects/types";
import { defaultLayerName, sanitizeLayerName, uniqueLayerName } from "./layerNames";
import {
  LAYER_PRESETS,
  MAX_RASTER_LAYERS,
  MAX_TOTAL_LAYERS,
  type LayerPreset,
  type PersistedLayerState,
} from "./layerTypes";
import { normalizeLayerOrder, validateLayerDocument } from "./layerValidation";

export interface LayerControllerState {
  activeLayerId: string;
  layers: PersistedLayerState[];
}

function cloneState(state: Readonly<LayerControllerState>): LayerControllerState {
  return {
    activeLayerId: state.activeLayerId,
    layers: state.layers.map((layer) => ({ ...layer })),
  };
}

export class LayerController {
  private state: LayerControllerState;

  public constructor(
    initial: Readonly<LayerControllerState>,
    private readonly projectType: ProjectType,
  ) {
    this.state = validateLayerDocument(initial.layers, initial.activeLayerId, projectType);
  }

  public getSnapshot(): LayerControllerState {
    return cloneState(this.state);
  }

  public replace(state: Readonly<LayerControllerState>): void {
    this.state = validateLayerDocument(state.layers, state.activeLayerId, this.projectType);
  }

  public getActive(): PersistedLayerState {
    return this.state.layers.find((layer) => layer.id === this.state.activeLayerId)!;
  }

  public activate(layerId: string): boolean {
    if (!this.state.layers.some((layer) => layer.id === layerId)) return false;
    if (this.state.activeLayerId === layerId) return false;
    this.state.activeLayerId = layerId;
    return true;
  }

  public add(preset: LayerPreset, id: string = crypto.randomUUID()): boolean {
    const rasterCount = this.state.layers.filter((layer) => layer.type === "raster").length;
    if (rasterCount >= MAX_RASTER_LAYERS || this.state.layers.length >= MAX_TOTAL_LAYERS)
      return false;
    const definition = LAYER_PRESETS.find((candidate) => candidate.role === preset);
    if (!definition) return false;
    const names = this.state.layers.map((layer) => layer.name);
    const layer: PersistedLayerState = {
      id,
      name: uniqueLayerName(defaultLayerName(definition.role), names),
      type: "raster",
      role: definition.role,
      system: false,
      visible: true,
      locked: false,
      opacity: 1,
      order:
        definition.role === "background"
          ? 0
          : Math.max(
              0,
              this.state.layers.findIndex((item) => item.id === this.state.activeLayerId) + 1,
            ),
    };
    const layers = [...this.state.layers];
    layers.splice(layer.order, 0, layer);
    this.state = { activeLayerId: id, layers: normalizeLayerOrder(layers) };
    return true;
  }

  public rename(layerId: string, requestedName: unknown): boolean {
    const layer = this.state.layers.find((candidate) => candidate.id === layerId);
    if (!layer || layer.system || layer.locked) return false;
    const sanitized = sanitizeLayerName(requestedName);
    if (!sanitized) return false;
    const next = uniqueLayerName(
      sanitized,
      this.state.layers
        .filter((candidate) => candidate.id !== layerId)
        .map((candidate) => candidate.name),
    );
    if (next === layer.name) return false;
    layer.name = next;
    return true;
  }

  public setVisible(layerId: string, visible: boolean): boolean {
    const layer = this.state.layers.find((candidate) => candidate.id === layerId);
    if (!layer || layer.visible === visible) return false;
    layer.visible = visible;
    return true;
  }

  public setLocked(layerId: string, locked: boolean): boolean {
    const layer = this.state.layers.find((candidate) => candidate.id === layerId);
    if (!layer || layer.locked === locked) return false;
    layer.locked = locked;
    return true;
  }

  public setOpacity(layerId: string, opacity: number): boolean {
    const layer = this.state.layers.find((candidate) => candidate.id === layerId);
    const next = Math.min(1, Math.max(0, Number.isFinite(opacity) ? opacity : 1));
    if (!layer || layer.locked || Math.abs(layer.opacity - next) < 0.0001) return false;
    layer.opacity = next;
    return true;
  }

  public move(layerId: string, direction: -1 | 1): boolean {
    const index = this.state.layers.findIndex((layer) => layer.id === layerId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= this.state.layers.length) return false;
    const layer = this.state.layers[index]!;
    if (layer.locked) return false;
    const targetLayer = this.state.layers[target]!;
    if ((layer.role === "background") !== (targetLayer.role === "background")) return false;
    const layers = [...this.state.layers];
    [layers[index], layers[target]] = [layers[target]!, layers[index]!];
    this.state.layers = layers.map((candidate, order) => ({ ...candidate, order }));
    return true;
  }

  public delete(layerId: string): boolean {
    const layer = this.state.layers.find((candidate) => candidate.id === layerId);
    const rasterCount = this.state.layers.filter((candidate) => candidate.type === "raster").length;
    if (!layer || layer.system || layer.locked || layer.type !== "raster" || rasterCount <= 1) {
      return false;
    }
    const index = this.state.layers.indexOf(layer);
    const layers = normalizeLayerOrder(
      this.state.layers.filter((candidate) => candidate.id !== layerId),
    );
    const replacement = layers[Math.min(index, layers.length - 1)] ?? layers[0]!;
    this.state = {
      activeLayerId:
        this.state.activeLayerId === layerId ? replacement.id : this.state.activeLayerId,
      layers,
    };
    return true;
  }

  public getMergeTarget(layerId: string): PersistedLayerState | undefined {
    const ordered = [...this.state.layers].sort((left, right) => left.order - right.order);
    const index = ordered.findIndex((layer) => layer.id === layerId);
    const source = ordered[index];
    const target = ordered[index - 1];
    return source?.type === "raster" && target?.type === "raster" ? target : undefined;
  }

  public canDraw(): boolean {
    const active = this.getActive();
    return active.type === "raster" && active.visible && !active.locked;
  }

  public canEditObjects(): boolean {
    const active = this.getActive();
    return active.type === "object" && active.visible && !active.locked;
  }
}
